import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import U2000SaveCancelDialog from '../../u2000Core/components/dialog/U2000SaveCancelDialog';
import { NS } from '../U3400';
import { useU3400Context } from '../U3400Context';
import type { U3400TileLogicalUnitCode } from '../U3400Service';
import { useU3400DashboardHandlers } from '../hooks/useU3400DashboardHandlers';
import { u3400MoveTileInDraft, u3400ToggleTileInDraft } from '../utils/u3400LayoutTiles';

interface U3400TileManagerDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

function U3400TileManagerDialog({ isOpen, onClose }: U3400TileManagerDialogProps) {
  const { t, i18n } = useTranslation(NS);
  const { initLU, layout } = useU3400Context();
  const { updateLayout } = useU3400DashboardHandlers();
  const [draftTiles, setDraftTiles] = useState<U3400TileLogicalUnitCode[]>(layout.tiles ?? []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDraftTiles(layout.tiles ?? []);
    }
  }, [isOpen, layout.tiles]);

  const isEn = i18n.language?.toLowerCase().startsWith('en');
  const maxTiles = initLU.maxTiles;

  const catalogueOptions = useMemo(
    () =>
      initLU.catalogueTiles.map((tile) => ({
        code: tile.code,
        label: isEn ? tile.labelEn : tile.labelFr,
      })),
    [initLU.catalogueTiles, isEn],
  );

  const labelFor = (code: U3400TileLogicalUnitCode) => {
    const fromCatalogue = catalogueOptions.find((o) => o.code === code);
    return fromCatalogue?.label ?? t(`tiles.${code}`);
  };

  const dirty =
    draftTiles.length !== (layout.tiles?.length ?? 0) ||
    draftTiles.some((code, i) => code !== layout.tiles[i]);

  const handleToggle = (code: U3400TileLogicalUnitCode, checked: boolean) => {
    setDraftTiles((prev) => u3400ToggleTileInDraft(prev, code, checked, maxTiles));
  };

  const handleMove = (index: number, direction: 'up' | 'down') => {
    setDraftTiles((prev) => u3400MoveTileInDraft(prev, index, direction));
  };

  const handleClose = async (action: 'save' | 'cancel') => {
    if (action === 'cancel') {
      onClose();
      return;
    }
    if (!dirty) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const next = await updateLayout(draftTiles, { optimistic: true });
      if (next) {
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <U2000SaveCancelDialog
      isOpen={isOpen}
      onClose={(action) => {
        void handleClose(action);
      }}
      title={t('manager.title')}
      width="medium"
      isValid={!saving}
    >
      <div className="flex flex-col gap-4 p-1">
        <p className="text-sm text-muted-foreground">
          {t('manager.subtitle', { max: maxTiles })}
        </p>

        <div className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600">
            {t('manager.catalogue')}
          </h3>
          <ul className="flex flex-col gap-2">
            {catalogueOptions.map((opt) => {
              const checked = draftTiles.includes(opt.code);
              const atMax = !checked && draftTiles.length >= maxTiles;
              return (
                <li key={opt.code}>
                  <label
                    className={`flex cursor-pointer items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm ${
                      atMax ? 'opacity-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded-sm border-gray-300"
                      checked={checked}
                      disabled={atMax || saving}
                      onChange={(e) => handleToggle(opt.code, e.target.checked)}
                    />
                    <span>{opt.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600">
            {t('manager.order')}
          </h3>
          {draftTiles.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('manager.orderEmpty')}</p>
          ) : (
            <ol className="flex flex-col gap-2">
              {draftTiles.map((code, index) => (
                <li
                  key={code}
                  className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2"
                >
                  <span className="text-sm font-medium text-gray-800">
                    {index + 1}. {labelFor(code)}
                  </span>
                  <div className="flex shrink-0 gap-1">
                    <button
                      type="button"
                      className="rounded-sm border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                      disabled={index === 0 || saving}
                      onClick={() => handleMove(index, 'up')}
                      title={t('manager.moveUp')}
                    >
                      {t('manager.moveUp')}
                    </button>
                    <button
                      type="button"
                      className="rounded-sm border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                      disabled={index === draftTiles.length - 1 || saving}
                      onClick={() => handleMove(index, 'down')}
                      title={t('manager.moveDown')}
                    >
                      {t('manager.moveDown')}
                    </button>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3400TileManagerDialog;
